// Lib
import type * as Immutable from 'immutable';

// Selectors
import { getAttachments, getAttachmentsProgressByActionType } from './attachmentsSelector';
import { getElements } from '../selectors/elementsSelector';
import { createShallowSelector } from '../../utils/milanoteReselect/milanoteReselect';

// Constants
import { ELEMENT_ATTACHMENT_ACTION_TYPES } from '../../../common/elements/elementConstants';
import { isBackgroundRemovalAttachmentId } from './attachmentIds';

// Types
import type { ImMNElementMap } from '../../../common/elements/elementModelTypes';

export type FileTransferToastSelectorProps = {
    elementId?: string;
};

const getToastElementId = (_state: unknown, ownProps: FileTransferToastSelectorProps) => ownProps?.elementId;

const isOwnedByToast = (
    elements: ImMNElementMap,
    attachmentElementId: string | undefined,
    elementId: string | undefined,
) => {
    if (!elementId) return true;
    if (!attachmentElementId) return false;

    return attachmentElementId === elementId && elements.has(elementId);
};

/**
 * Checks whether an attachment has enough transfer progress to show the toast.
 */
const hasAttachmentTransferProgress = (attachment: Immutable.Map<string, unknown>) => {
    const fetching = !!attachment.get('fetching');
    const loaded = !!attachment.get('loaded');

    return fetching && loaded;
};

const getFileTransferToastAttachments = createShallowSelector(
    getAttachments,
    getElements,
    getToastElementId,
    (attachments, elements: ImMNElementMap, elementId) =>
        attachments.filter((attachment: Immutable.Map<string, unknown>) => {
            const attachmentElementId = attachment.get('id') as string | undefined;

            if (isBackgroundRemovalAttachmentId(attachmentElementId)) return false;

            return isOwnedByToast(elements, attachmentElementId, elementId);
        }),
);

export const fileTransferToastSelector = createShallowSelector(getFileTransferToastAttachments, (attachments) => {
    const uploadData = getAttachmentsProgressByActionType(attachments, ELEMENT_ATTACHMENT_ACTION_TYPES.UPLOAD);
    const progress = uploadData.count
        ? uploadData
        : getAttachmentsProgressByActionType(attachments, ELEMENT_ATTACHMENT_ACTION_TYPES.DOWNLOAD);

    return {
        isTransferringAttachmentWithProgress: attachments.some(hasAttachmentTransferProgress),
        progress,
    };
});
